import { assertPartialSchema, createAction, useSelector, v } from "../../../lib";
import { toHydratedDate } from "../../serializable";

export enum ParticipantIssueActionType {
  PARTICIPANT_ISSUE_SET = "PARTICIPANT_ISSUE_SET",
}

export const doPARTICIPANT_ISSUE_SET = createAction(
  ParticipantIssueActionType.PARTICIPANT_ISSUE_SET,
);

export interface ParticipantIssuesPaylaod {
  issuesIndexMap: IssuesIndexMap;
  participantIssuesMap: ParticipantIssuesMap;
  garminConnectLinkDuplicatedParticiipantSetList?: GarminConnectLinkDuplicatedParticiipantSetList;
}

export const ParticipantIssuesPaylaodSchema = v.object({
  issuesIndexMap: v.object().pattern(v.number(), v.string()),
  participantIssuesMap: v.object().pattern(
    v.string(),
    v.object({
      isignia: v.string().allow(""),
      issues: v.array().items(v.number()),
    }),
  ),
  garminConnectLinkDuplicatedParticiipantSetList: v
    .array()
    .items(v.array().items(v.string()))
    .optional(),
});

interface ParticipantIssueList {
  isignia: string;
  issues: number[];
}

type ParticipantIssuesMap = { [participantId: string]: ParticipantIssueList };
type IssuesIndexMap = { [index: number]: string };
type GarminConnectLinkDuplicatedParticiipantSetList = string[][];

interface ParticipantIssueState {
  updatedDate: string;
  issuesIndexMap: IssuesIndexMap;
  participantIssuesMap: ParticipantIssuesMap;
  garminConnectLinkDuplicatedParticiipantSetList?: GarminConnectLinkDuplicatedParticiipantSetList;
}

export interface ParticipantIssue {
  updatedDate: Date;
  issuesIndexMap: IssuesIndexMap;
  participantIssuesMap: ParticipantIssuesMap;
  garminConnectLinkDuplicatedParticiipantSetList?: GarminConnectLinkDuplicatedParticiipantSetList;
}

interface RootState {
  participantIssue: ParticipantIssueState;
}

let cachedParticipantIssueRaw: ParticipantIssueState | null = null;
let cachedParticipantIssueHydrated: ParticipantIssue | null = null;

/* selector */
export const useSelectParticipantIssue = () => {
  return useSelector((state: RootState) => hydrateParticipantIssue(state.participantIssue));
};

export const participantIssueActionCreators = {
  doPARTICIPANT_ISSUE_SET,
};

export const participantIssueDefaultState: ParticipantIssueState = {
  updatedDate: new Date().toISOString(),
  issuesIndexMap: {},
  participantIssuesMap: {},
};

type Action = {
  type: ParticipantIssueActionType.PARTICIPANT_ISSUE_SET;
  payload: ParticipantIssuesPaylaod;
};

export const participantIssueReducer = (
  state = { ...participantIssueDefaultState },
  { type, payload }: Action,
): ParticipantIssueState => {
  switch (type) {
    case ParticipantIssueActionType.PARTICIPANT_ISSUE_SET:
      assertPartialSchema({
        payload,
        schema: ParticipantIssuesPaylaodSchema,
      });
      return {
        updatedDate: new Date().toISOString(),
        ...payload,
      };

    default:
      return state;
  }
};

export const hydrateParticipantIssue = (
  participantIssueState: ParticipantIssueState,
): ParticipantIssue => {
  if (cachedParticipantIssueRaw === participantIssueState && cachedParticipantIssueHydrated) {
    return cachedParticipantIssueHydrated;
  }

  const hydratedParticipantIssue: ParticipantIssue = {
    ...participantIssueState,
    updatedDate: toHydratedDate(participantIssueState.updatedDate) as Date,
  };

  cachedParticipantIssueRaw = participantIssueState;
  cachedParticipantIssueHydrated = hydratedParticipantIssue;

  return hydratedParticipantIssue;
};
